import { AxiosResponse } from 'axios';
import {
  downloadOnFarmInventoryTransactionReport,
  getOnFarmInventoryTransactionById,
  getOnFarmInventoryTransactions,
  getOnFarmInventoryTransactionsOdata,
} from '../api/onFarmTransactionsApi';
import { InventoryTransaction } from '../types/interfaces';
import { OnFarmAnimal } from '../types/interfaces/animal.interfaces';
import { OnFarmSpecimen } from '../types/interfaces/onFarmSpecimen.interfaces';

type OnFarmInventoryTransactionDto = Partial<InventoryTransaction> & {
  onFarmInventoryTransactionId?: number;
  onFarmTransactionId?: number;
  lastUpdated?: Date | string;
  onFarmSpecimenId?: number | null;
  onFarmSpecimen?: OnFarmSpecimen | null;
};

const toValidDate = (value?: Date | string): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getFullYear() <= 1) return undefined;
  return date;
};

const mapOnFarmAnimal = (animal?: OnFarmAnimal | null) => {
  if (!animal) return undefined;

  return {
    ...animal,
    animalId: animal.animalId ?? animal.onFarmAnimalId,
  };
};

const mapOnFarmSpecimen = (onFarmSpecimen?: OnFarmSpecimen | null) => {
  if (!onFarmSpecimen) return undefined;

  const onFarmAnimal = mapOnFarmAnimal(onFarmSpecimen.onFarmAnimal);

  return {
    specimenId: onFarmSpecimen.onFarmSpecimenId,
    animalId: onFarmSpecimen.onFarmAnimalId ?? onFarmAnimal?.animalId ?? 0,
    animal: onFarmAnimal,
    lotNumber: onFarmSpecimen.lotNumber,
    freezeDate: toValidDate(onFarmSpecimen.freezeDate),
  };
};

const mapOnFarmTransaction = (transaction: OnFarmInventoryTransactionDto): InventoryTransaction => {
  const createdDatetime = toValidDate(transaction.createdDatetime) ?? toValidDate(transaction.lastUpdated);
  const modifiedDatetime = toValidDate(transaction.modifiedDatetime) ?? createdDatetime;
  const mappedSpecimen = transaction.specimen ?? mapOnFarmSpecimen(transaction.onFarmSpecimen);

  return {
    ...transaction,
    inventoryTransactionId:
      transaction.onFarmInventoryTransactionId ?? transaction.onFarmTransactionId ?? transaction.inventoryTransactionId,
    specimenId: transaction.specimenId ?? mappedSpecimen?.specimenId ?? transaction.onFarmSpecimenId ?? undefined,
    specimen: mappedSpecimen,
    createdDatetime: createdDatetime as Date,
    modifiedDatetime: modifiedDatetime as Date,
  } as InventoryTransaction;
};

export const getOnFarmAccountTransactions = async (query?: {}): Promise<AxiosResponse<InventoryTransaction[], any>> => {
  const response = await getOnFarmInventoryTransactions(query);
  return { ...response, data: ((response.data ?? []) as OnFarmInventoryTransactionDto[]).map(mapOnFarmTransaction) };
};

export const getOnFarmAccountTransactionsOdata = async (query?: {}): Promise<AxiosResponse<InventoryTransaction[], any>> => {
  const response = await getOnFarmInventoryTransactionsOdata(query);
  return { ...response, data: ((response.data ?? []) as OnFarmInventoryTransactionDto[]).map(mapOnFarmTransaction) };
};

export const getOnFarmTransactionById = async (id: string | number): Promise<AxiosResponse<InventoryTransaction, any>> => {
  const response = await getOnFarmInventoryTransactionById(Number(id), { include: 'OnFarmSpecimen.OnFarmAnimal' });
  return { ...response, data: mapOnFarmTransaction(response.data as OnFarmInventoryTransactionDto) };
};

export const downloadOnFarmTransactionHistoryReport = async (payload?: {}, query?: {}, fileName?: string) => {
  const response = await downloadOnFarmInventoryTransactionReport(payload, query);
  const blob = new Blob([response.data], { type: response.headers['content-type'] });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  if (fileName) {
    link.setAttribute('download', fileName);
  }
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
